import PropTypes from 'prop-types';
import React from 'react';
import get from 'lodash/get';
import optionsFactory from '../../../events/options-factory';
import { trackMagnifierDragged } from '../../../analytics/category/magnifier';
import './Magnifier.scss';

const getEventData = event => get(event, 'targetTouches[0]', event);
const getXY = (x, y, bounds) => ({
  x: x - bounds.left,
  y: y - bounds.top,
  isTouch: !!event.targetTouches
});

const eventOptions = optionsFactory({
  passive: false,
  useCapture: false
});

class Magnifier extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      x: 0,
      y: 0,
      bounds: null,
      isOver: false,
      isTouch: false,
    };

    this.startTime = null;

    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handlePreventScroll = this.handlePreventScroll.bind(this);
    this.handleContext = this.handleContext.bind(this);
  }

  handlePreventScroll(event) {
    if (this.dragging) {
      event.preventDefault();
    }
  }

  componentDidMount() {
    // prevent ios from scrolling when dragging
    document.body.addEventListener('touchstart', this.handlePreventScroll, eventOptions);
    document.body.addEventListener('touchmove', this.handlePreventScroll, eventOptions);
  }

  componentWillUnmount() {
    document.body.removeEventListener('touchstart', this.handlePreventScroll, eventOptions);
    document.body.removeEventListener('touchmove', this.handlePreventScroll, eventOptions);
  }

  handleEnd(event) {
    if (!this.props.active) {
      return;
    }
    event.stopPropagation();
    if (this.dragging) {
      const millis = Date.now() - this.startTime;
      const seconds = Math.floor(millis / 1000);
      trackMagnifierDragged(this.props.productId, seconds);
    }
    this.dragging = false;
    this.setState(() => ({
      bounds: null,
      isOver: false
    }));
  };

  handleUpdate(event) {
    if (!this.props.active) {
      return;
    }
    if (!this.dragging) {
      this.startTime = Date.now();
    }
    this.dragging = true;
    event.stopPropagation();
    const bounds = event.target.getBoundingClientRect();
    const { clientX, clientY, isTouch } = getEventData(event);
    const { x, y } = getXY(clientX, clientY, bounds);
    const isOver = x >= 0 && x <= bounds.width && y >= 0 && y <= bounds.height;
    this.setState({
      x,
      y,
      bounds,
      isTouch,
      isOver,
    });
  };

  handleContext(event) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  setRef(ref) {
    this._container = ref;
  }

  render() {
    if (!this.props.active) {
      return this.props.children;
    }

    const img = React.cloneElement(
      React.Children.only(this.props.children),
      {
        onTouchStart: this.handleUpdate,
        onTouchMove: this.handleUpdate,
        onTouchEnd: this.handleEnd,

        onMouseOver: this.handleUpdate,
        onMouseMove: this.handleUpdate,
        onMouseOut: this.handleEnd,
        onWheel: this.handleEnd,

        onContextMenu: this.handleContext,
      }
    );

    if (!this.state.isOver || !this.props.imageUrl) {
      return (
        <div className="magnifier">
          {img}
        </div>
      );
    }

    const { zoom, radius, borderWidth, borderColor, boxShadow, imageUrl } = this.props;
    const { x, y } = this.state;
    const { width, height, left, top } = this.state.bounds;

    const offsetX = -radius;
    const offsetY = this.props.media.tabletDown ? -(radius * 2) : -radius ;

    const xRatio = (x - radius / 2) / (width - radius);
    const yRatio = (y - radius / 2) / (height - radius);
    const bkgPosX = `${xRatio * 100}%`;
    const bkgPosY = `${yRatio * 100}%`;

    return (
      <div className="magnifier">
        {img}
        <div
          style={{
            pointerEvents: 'none',
            touchAction: 'none',
            userSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitUserDrag: 'none',
            WebkitUserSelect: 'none',

            position: 'fixed',
            zIndex: 100000,
            width: radius * 2,
            height: radius * 2,
            left: left + x + offsetX,
            top: top + y + offsetY,
            backgroundColor: '#FFF',
            backgroundImage: `url(${imageUrl})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${width * zoom}px ${height * zoom}px`,
            backgroundPosition: `${bkgPosX} ${bkgPosY}`,
            borderRadius: radius * 2,
            border: `${borderWidth}px solid ${borderColor}`,
            boxShadow
          }}
        />
      </div>
    );
  }
}

Magnifier.defaultProps = {
  zoom: 2,
  radius: 75,
  borderWidth: 8,
  borderColor: '#d8e0e0',
  boxShadow: '0px 0px 15px 0px #000000',
  imageUrl: null,
};

Magnifier.propTypes = {
  productId: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
  active: PropTypes.bool,
  zoom: PropTypes.number,
  radius: PropTypes.number,
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
  boxShadow: PropTypes.string,
  media: PropTypes.shape({
    tabletDown: PropTypes.bool.isRequired
  }).isRequired,
};

export default Magnifier;





// WEBPACK FOOTER //
// ./src/client/js/view/components/Magnifier/Magnifier.js